import { useNavigate } from 'react-router-dom'
import { QuoteItem_, useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { useDesignsApi } from 'src/api/db/apiDesigns'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { TableRow } from 'src/shared/components/ui/table'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { ProductPreview } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview'
import { ChangeDecorationDialog } from 'src/pages/_myAccount/EditDecoration/components/ChangeDecorationDialog'
import { UpdateQuantityDialog } from './components/expanded/UpdateQuantityDialog'
import { DeleteConfirmDialog } from '../../shared/DeleteConfirmDialog'
import { useDebouncedFieldUpdate } from '../../../hooks'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { ProductRowDescriptionCell } from './components/cells/ProductRowDescriptionCell'
import { ProductRowDiscountCell } from './components/cells/ProductRowDiscountCell'
import { ProductRowTotalCell } from './components/cells/ProductRowTotalCell'
import { ProductRowExpandedSection } from './components/expanded/ProductRowExpandedSection'
import {
  QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE,
  QUOTE_ITEM_ROW_STANDALONE,
  QUOTE_ITEM_ROW_GROUP_START,
} from '../../../consts'
import { cn } from 'src/shared/components/ui/utils'
import {
  calculateProductRowBreakdown,
  getProductReceiptLines,
} from '../../../utils'

export const ProductRow = ({
  quote_item,
  isEditMode = false,
  showDiscountColumn = true,
  isReadOnly = false,
}: {
  quote_item: QuoteItem_
  isEditMode?: boolean
  showDiscountColumn?: boolean
  isReadOnly?: boolean
}) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [changeDecorationSubItemId, setChangeDecorationSubItemId] = useState<
    number | null
  >(null)

  const product_variant_color =
    quote_item.product?.product_variant?.[0]?.product_variant_color?.find(
      (color) => color.id === quote_item.product_variant_color_id
    )

  const sorted_product_images = useMemo(() => {
    const images = product_variant_color?.product_image
    if (!images) return []
    return [...images].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
  }, [product_variant_color?.product_image])

  const product_variant_colors = (quote_item.product?.product_variant?.[0]
    ?.product_variant_color || []) as ProductVariantColor_[]

  const { t } = useLocalization()
  const isAdmin = useIsAdmin()

  const { updateQuoteItem, deleteQuoteItem } = useQuoteItemApi({})
  const { createQuoteSubItem, deleteQuoteSubItem } = useQuoteSubItemApi()
  const { createDesign } = useDesignsApi()
  const navigate = useNavigate()

  const [quantity, setQuantity] = useState(quote_item.quantity || 1)
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false)
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateUnitPrice = useCallback(
    (value: number) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          unit_price: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateDescription = useCallback(
    (value: string) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          description: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateDiscount = useCallback(
    (value: number) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          discount: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateNotes = useCallback(
    (value: string) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          notes: value.trim() === '' ? null : value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )

  const [unitPriceLocal, , handleUnitPriceChange] = useDebouncedFieldUpdate(
    quote_item.unit_price || 0,
    updateUnitPrice
  )
  const [descriptionLocal, , handleDescriptionChange] = useDebouncedFieldUpdate(
    quote_item.description || '',
    updateDescription
  )
  const [discountLocal, , handleDiscountChange] = useDebouncedFieldUpdate(
    quote_item.discount || 0,
    updateDiscount
  )
  const [notesLocal, , handleNotesChange] = useDebouncedFieldUpdate(
    quote_item.notes ?? '',
    updateNotes
  )

  const unitPrice = isEditMode ? unitPriceLocal : quote_item.unit_price || 0
  const discount = isEditMode ? discountLocal : quote_item.discount || 0
  const total = Math.max(0, unitPrice * quantity - discount)
  const breakdown = useMemo(
    () =>
      calculateProductRowBreakdown({
        ...quote_item,
        unit_price: unitPrice,
        quantity,
        discount,
      }),
    [quote_item, unitPrice, quantity, discount]
  )
  const receiptLines = useMemo(
    () => getProductReceiptLines(quote_item, t, { unitPrice, quantity, discount }),
    [quote_item, unitPrice, quantity, discount, t]
  )
  const hasSubItems = (quote_item.quote_sub_item?.length ?? 0) > 0
  const showExpandedSection =
    isEditMode && expanded && (hasSubItems || !isReadOnly)

  const handleDeleteSubItem = async (subItemId: number) => {
    const subItem = quote_item.quote_sub_item?.find((s) => s.id === subItemId)
    if (subItem?.type === 'decoration') {
      const linkedSetup = quote_item.quote_sub_item?.find(
        (qsi) =>
          qsi.type === 'setup_cost' &&
          // @ts-ignore data links setup cost to decoration
          qsi.data?.quote_sub_item_id === subItemId
      )
      if (linkedSetup) {
        await deleteQuoteSubItem.mutateAsync(linkedSetup.id)
      }
    }
    deleteQuoteSubItem.mutate(subItemId)
  }

  useEffect(() => {
    setQuantity(quote_item.quantity || 1)
  }, [quote_item.quantity])

  const debouncedUpdateQuantity = (newQuantity: number) => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    debounceTimeoutRef.current = setTimeout(() => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          quantity: newQuantity,
        })
      }
    }, 500)
  }

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    }
  }, [])

  const handleQuantityChange = (newQuantity: number) => {
    const moq = quote_item.product?.moq || 1
    if (newQuantity >= moq) {
      setQuantity(newQuantity)
      debouncedUpdateQuantity(newQuantity)
    }
  }

  const proceedWithDecoration = async (qty: number) => {
    if (qty !== quantity && quote_item.id && quote_item.quote_id) {
      await updateQuoteItem.mutateAsync({
        id: quote_item.id,
        quote_id: quote_item.quote_id,
        quantity: qty,
      })
    }

    const newQuoteSubItem = await createQuoteSubItem.mutateAsync({
      quote_item_id: quote_item.id,
      type: 'decoration',
      description: t('New decoration', 'Ny dekoration'),
      quantity: qty,
    })

    await createDesign.mutateAsync({
      quote_sub_item_id: newQuoteSubItem.data?.id,
      w: 100,
      h: 100,
    })

    navigate(
      `${paths.requests}/${quote_item.quote_id}/order_items/${quote_item.id}/sub_item/${newQuoteSubItem.data?.id} `
    )
  }

  const handleAddDecoration = () => {
    if (quantity === 1) {
      setQuantityDialogOpen(true)
    } else {
      proceedWithDecoration(quantity)
    }
  }

  const handleEditDecoration = (subItemId: number) => {
    navigate(
      `${paths.requests}/${quote_item.quote_id}/order_items/${quote_item.id}/sub_item/${subItemId}`
    )
  }

  const handleAddCustomSubRow = async () => {
    await createQuoteSubItem.mutateAsync({
      quote_item_id: quote_item.id,
      type: QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE,
      description: t('New line', 'Ny rad'),
      quantity: 1,
      unit_price: 0,
    })
  }

  const subtotalBeforeDiscount = unitPrice * quantity

  return (
    <>
      <TableRow
        className={cn(
          showExpandedSection
            ? QUOTE_ITEM_ROW_GROUP_START
            : QUOTE_ITEM_ROW_STANDALONE,
          'group'
        )}
      >
        <ProductRowDescriptionCell
          quote_item={quote_item}
          sorted_product_images={sorted_product_images}
          product_variant_color={
            product_variant_color as ProductVariantColor_ | undefined
          }
          product_variant_colors={product_variant_colors}
          isReadOnly={isReadOnly}
          isEditMode={isEditMode}
          onOpenPreview={() => setPreviewOpen(true)}
          descriptionLocal={descriptionLocal}
          onDescriptionChange={handleDescriptionChange}
          colorPickerOpen={colorPickerOpen}
          onColorPickerOpenChange={setColorPickerOpen}
          onSelectColor={async (color_id: number) => {
            await updateQuoteItem.mutateAsync({
              id: quote_item.id,
              quote_id: quote_item.quote_id!,
              product_variant_color_id: color_id,
            })
          }}
          onAddDecoration={handleAddDecoration}
          onEditDecoration={handleEditDecoration}
          onChangeDecoration={setChangeDecorationSubItemId}
          notesLocal={notesLocal}
          onNotesChange={handleNotesChange}
          t={t}
        />

        {showDiscountColumn && (
          <ProductRowDiscountCell
            isEditMode={isEditMode}
            discountLocal={discountLocal}
            onDiscountChange={handleDiscountChange}
            discount={discount}
          />
        )}

        <ProductRowTotalCell
          discount={discount}
          subtotalBeforeDiscount={subtotalBeforeDiscount}
          total={total}
          showDelete={!isReadOnly || isEditMode}
          onDeleteClick={() => setDeleteDialogOpen(true)}
          priceSummaryLines={receiptLines}
          lineTotal={breakdown.total}
          isEditMode={isEditMode}
          productUnitPrice={unitPrice}
          onProductUnitPriceChange={
            isEditMode ? handleUnitPriceChange : undefined
          }
          onDiscountChange={isEditMode ? handleDiscountChange : undefined}
          onDeleteSubItem={isEditMode ? handleDeleteSubItem : undefined}
          onEditDecoration={!isReadOnly ? handleEditDecoration : undefined}
          onChangeDecoration={
            !isReadOnly ? setChangeDecorationSubItemId : undefined
          }
          onAddDecoration={
            isEditMode && !isReadOnly ? handleAddDecoration : undefined
          }
          onAddCustomLine={
            isEditMode && isAdmin && !isReadOnly
              ? handleAddCustomSubRow
              : undefined
          }
          canAddCustomLine={isAdmin}
          quantity={quantity}
          onQuantityChange={handleQuantityChange}
          isReadOnly={isReadOnly}
        />
      </TableRow>

      {showExpandedSection && (
        <ProductRowExpandedSection
          quote_item={quote_item}
          isEditMode={isEditMode}
          isAdmin={isAdmin}
          isReadOnly={isReadOnly}
          showDiscountColumn={showDiscountColumn}
          onAddDecoration={handleAddDecoration}
          onAddCustomSubRow={handleAddCustomSubRow}
          onDeleteQuoteSubItem={(id) => deleteQuoteSubItem.mutate(id)}
        />
      )}

      {quote_item.product?.id && (
        <ProductPreview
          open={previewOpen}
          setOpen={setPreviewOpen}
          product_id={quote_item.product.id}
          chosen_product_variant_color={
            (product_variant_color as ProductVariantColor_ | undefined) ||
            (product_variant_colors?.[0] as ProductVariantColor_)
          }
          product_variant_colors={product_variant_colors}
          product_name={quote_item.product?.item_name || ''}
          product_description={quote_item.product?.item_description || ''}
          moq={quote_item.product?.moq || 1}
          article_number={quote_item.product?.article_number || ''}
          price={quote_item.product?.price || 0}
          klayder_recommends={quote_item.product?.klayder_recommends || false}
          klayder_recommendation_text={
            quote_item.product?.klayder_recommendation_text || ''
          }
        />
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('Delete product', 'Ta bort produkt')}
        description={`${t(
          'Are you sure you want to delete',
          'Är du säker på att du vill ta bort'
        )} "${
          quote_item.product?.item_name || t('this product', 'denna produkt')
        }"? ${t(
          'This action cannot be undone.',
          'Denna åtgärd kan inte ångras.'
        )}`}
        onConfirm={() =>
          deleteQuoteItem.mutate({
            id: quote_item.id,
            quote_id: quote_item.quote_id!,
          })
        }
      />

      <UpdateQuantityDialog
        open={quantityDialogOpen}
        onOpenChange={setQuantityDialogOpen}
        initialQuantity={quantity}
        moq={quote_item.product?.moq ?? 1}
        onContinue={proceedWithDecoration}
      />

      <ChangeDecorationDialog
        open={changeDecorationSubItemId != null}
        onOpenChange={(open) => {
          if (!open) setChangeDecorationSubItemId(null)
        }}
        quoteItemId={quote_item.id}
        quoteSubItemId={changeDecorationSubItemId ?? 0}
      />
    </>
  )
}
